<script setup lang="ts">
  import { getClientHttpErrorMessage, getErrorMessage } from '~/utils/errors'

  // Idioma
  const { locale, t } = useI18n()

  // Toast
  const toast = useToast()

  // Meta del SEO
  definePageMeta({
    layout: 'simple',
    showLanguageSelector: false
  })

  useSeoMeta({
    title: t('checkout.meta.title'),
    description: t('checkout.meta.description')
  })

  // Stores
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)
  const checkoutStore = useCheckoutStore()

  const isLoading = ref(true)
  const route = useRoute()

  const checkoutData = computed((): CheckoutData & { hotelData: Hotel; roomData: Record<string, unknown> } => {
    const storeData = checkoutStore.getCheckoutData()

    const defaultData: CheckoutData & { hotelData: Hotel; roomData: Record<string, unknown> } = {
      date: '',
      packhour: 0,
      hour: '',
      numRooms: 0,
      countExtra: '',
      extras: '',
      totalAmount: 0,
      totalExtrasAmount: 0,
      hotelData: {
        id: 0,
        name: '',
        address: '',
        description: '',
        latitude: '',
        longitude: '',
        city: '',
        chain: '',
        stars: { id: 0, abbr: '', order: '', name: '' },
        services: [],
        pack_min_price: {},
        promo: [],
        images: [],
        images_rooms: [],
        tag: [],
        extras: [],
        infoAirport: {},
        rooms: {},
        checkin_times: {},
        slug: ''
      } as Hotel,
      roomData: {
        info: { name: '' }
      },
    }
    
    if (!storeData) {
      return defaultData
    }
    
    return {
      ...defaultData,
      ...storeData,
      hotelData: {
        ...defaultData.hotelData,
        ...storeData?.hotelData
      },
      roomData: {
        ...defaultData.roomData,
        ...storeData?.roomData
      }
    }
  })

  const priceData = ref({
    base: '',
    priceRoom: '',
    fee: '',
    total: 0,
    totalFormat: '',
    destinationFormat: '',
    destination: '',
    now: '',
    extras: '',
    specificTaxes: '',
    specificTaxesNum: 0,
    specificTaxesList: []
  })

  const extraData = ref([])

  const contactForm = ref({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    comments: ''
  })

  const promoData = ref({
    applied: false,
    discount: '',
    discountFormatted: '',
    newPrice: '',
    code: ''
  })

  const loadFormData = () => {
    try {
      const formData = sessionStorage.getItem('byhours_checkout_form')
      if (formData) {
        const parsed = JSON.parse(formData)
        contactForm.value = { ...contactForm.value, ...parsed }
      }
    } catch (error: unknown) {
      console.error('Error cargando datos del formulario:', getErrorMessage(error))
    }

    try {
      const promoDataStr = sessionStorage.getItem('byhours_promo_data')
      if (promoDataStr) {
        promoData.value = JSON.parse(promoDataStr)
      }
    } catch (error: unknown) {
      console.error('Error cargando datos promocionales:', getErrorMessage(error))
    }
  }

  const handlePaymentReturn = async () => {
    const paymentIntent = route.query.payment_intent as string
    const paymentIntentClientSecret = route.query.payment_intent_client_secret as string
    const redirectStatus = route.query.redirect_status as string

    if (!paymentIntent || !paymentIntentClientSecret) {
      toast.add({
        title: t('checkout.errors.title'),
        description: t('checkout.errors.paymentIntentError'),
        color: 'error'
      })
      await navigateTo(`/${locale.value}/v2/one-step-check-out/`)
      return
    }

    if (redirectStatus !== 'succeeded') {
      toast.add({
        title: t('checkout.errors.title'),
        description: t('checkout.errors.paymentIncompleteError', { status: redirectStatus || 'unknown' }),
        color: 'error'
      })
      await navigateTo(`/${locale.value}/v2/one-step-check-out/`)
      return
    }

    try {
      isLoading.value = true

      let bookingRef = ''
      const refFromStorage = sessionStorage.getItem('byhours_booking_reference')
      if (refFromStorage) {
        bookingRef = refFromStorage
      }

      if (!bookingRef) {
        const storeData = checkoutStore.getCheckoutData()
        bookingRef = storeData?.reference ?? ''
      }

      if (!bookingRef) {
        throw new Error(t('checkout.errors.bookingInitError'))
      }

      loadFormData()

      try {
        const priceDataStr = sessionStorage.getItem('byhours_price_data')
        if (priceDataStr) {
          priceData.value = JSON.parse(priceDataStr)
        }
      } catch (error: unknown) {
        console.error('Error cargando datos de precios:', getErrorMessage(error))
      }

      try {
        const extraDataStr = sessionStorage.getItem('byhours_extra_data')
        if (extraDataStr) {
          extraData.value = JSON.parse(extraDataStr)
        }
      } catch (error: unknown) {
        console.error('Error cargando datos de extras:', getErrorMessage(error))
      }

      const postBookingData = {
        reference: bookingRef,
        stripe_payment_intent_id: paymentIntent
      }

      let confirm: PostCheckoutApiResponse | null = null
      try {
        confirm = await $fetch<PostCheckoutApiResponse>(`${useBaseUrl()}/post-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: postBookingData
        })
      } catch (error: unknown) {
        const errorMessage = getClientHttpErrorMessage(error) || t('checkout.errors.bookingConfirmError')
        throw new Error(errorMessage)
      }

      if (!confirm || confirm?.status !== 'success') {
        const errorMessage = confirm?.message || confirm?.response?.message || t('checkout.errors.bookingConfirmError')
        throw new Error(errorMessage)
      }

      const holder = `${contactForm.value.firstName} ${contactForm.value.lastName}`.trim()

      const bookingData = {
        ...checkoutData.value,
        currency: currency.value,
        totalAmount: priceData.value.totalFormat,
        reference: bookingRef,
        holderName: holder,
        booking_code: bookingRef,
        stripePaymentIntentId: paymentIntent,
        stripePaymentMethodId: '',
        stripeCustomerToken: '',
        confirmed: true,
        holderFirstName: contactForm.value.firstName,
        holderLastName: contactForm.value.lastName,
        holderEmail: contactForm.value.email,
        holderPhone: contactForm.value.phone,
        comments: contactForm.value.comments,
        priceData: priceData.value,
        promoData: promoData.value.applied ? {
          applied: true,
          discount: promoData.value.discount,
          discountFormatted: promoData.value.discountFormatted,
          newPrice: promoData.value.newPrice,
          code: promoData.value.code
        } : null,
        extrasData: extraData.value || [],
        timestamp: Date.now(),
      }

      sessionStorage.setItem('byhours_booking_data', JSON.stringify(bookingData))

      checkoutStore.clearCheckoutData()

      await navigateTo(`/${locale.value}/v2/confirm`, { replace: true })
    } catch (error: unknown) {
      toast.add({
        title: t('checkout.errors.title'),
        description: getErrorMessage(error) || t('checkout.errors.unexpectedPaymentError'),
        color: 'error'
      })
      await navigateTo(`/${locale.value}/v2/one-step-check-out/`, { replace: true })
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    await handlePaymentReturn()
  })

</script>

<template>
  <div class="min-h-screen flex items-center justify-center">
    <div class="text-center">
      <div v-if="isLoading" class="space-y-4">
        <UIcon name="i-lucide:loader-circle" class="animate-spin text-4xl"/>
        <p class="text-gray-600">{{ t('checkout.loading') }}</p>
      </div>
    </div>
  </div>
</template>

